import { useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams, Link } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { GITHUB_CHANNEL } from "@/lib/github";
import { Card, Loading } from "@/components/common";
import { GraphiteScope } from "@/components/Graphite";
import { Button } from "@/components/ui/button";
import { Github, Check, X } from "lucide-react";

// The "Connect GitHub" popup (Settings), one page per step of GitHub's round trip:
//   /github/connect   start: get a one-time state, go to the App's install page on GitHub
//   /github/setup     back from installing (GitHub's setup URL, with installation_id): go authorise
//   /github/callback  back from authorising (code): the backend checks the installation is the owner's
// The state lives in this window's sessionStorage, so the round trip doesn't depend on GitHub echoing it.
// When done, the opener (Settings) hears it on a BroadcastChannel, and the popup closes itself.
const STATE_KEY = "cb_github_state";

function remember(state) {
  try { sessionStorage.setItem(STATE_KEY, state); } catch { /* private window: the query's state is used */ }
}
function recall() {
  try { return sessionStorage.getItem(STATE_KEY); } catch { return null; }
}

async function start() {
  const started = (await api.post("/github/connect")).data; // {state, install_url}
  remember(started.state);
  return started;
}

export default function GitHubConnect() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const [result, setResult] = useState(null); // {ok, text}
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode runs effects twice; each state works once
    ran.current = true;
    const step = pathname.split("/").pop();
    (async () => {
      try {
        if (step === "connect") {
          window.location.assign((await start()).install_url);
        } else if (step === "setup") {
          const installation = params.get("installation_id");
          if (!installation) throw new Error("GitHub didn't say which installation to use. Start again from Settings.");
          // arriving from GitHub on its own (e.g. after changing the app's repositories): a fresh state
          const state = params.get("state") || recall() || (await start()).state;
          const { authorize_url } = (await api.post("/github/setup", { state, installation_id: Number(installation) })).data;
          window.location.assign(authorize_url);
        } else if (step === "callback") {
          const code = params.get("code");
          const state = params.get("state") || recall();
          if (!code) throw new Error(params.get("error_description") || "GitHub didn't confirm the authorisation.");
          const { connection } = (await api.post("/github/callback", { state, code })).data;
          try { sessionStorage.removeItem(STATE_KEY); } catch { /* nothing to clean */ }
          try { new BroadcastChannel(GITHUB_CHANNEL).postMessage({ type: "connected" }); } catch { /* old browser */ }
          setResult({ ok: true, text: `GitHub connected: ${connection.account}.` });
          if (window.opener) setTimeout(() => window.close(), 1200);
        }
      } catch (e) {
        setResult({ ok: false, text: e?.response ? apiErr(e) : e.message });
      }
    })();
  }, [pathname, params]);

  return (
    // a page of its own (no sidebar): the scope covers the whole screen
    <GraphiteScope className="min-h-screen grid place-items-center p-6 bg-background" data-testid="github-connect">
      <Card className="max-w-sm w-full space-y-4 text-center">
        <Github className="w-8 h-8 mx-auto" />
        {!result && <><Loading /><p className="text-sm text-muted-foreground">Talking to GitHub…</p></>}
        {result && (
          <>
            <p className={`flex items-center justify-center gap-2 ${result.ok ? "text-gr-success" : "text-gr-warning"}`}>
              {result.ok ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />} {result.text}
            </p>
            {result.ok
              ? <p className="text-sm text-muted-foreground">You can close this window.</p>
              : <Button asChild variant="outline" size="sm"><Link to="/github/connect" reloadDocument>Try again</Link></Button>}
            {!window.opener && <Button asChild size="sm"><Link to="/founder/settings">Back to Settings</Link></Button>}
          </>
        )}
      </Card>
    </GraphiteScope>
  );
}
